import { Box, Typography, Tooltip } from '@mui/material'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import { memo } from 'react'

/**
 * Narrow vertical left-menu navigation for the mentor clinic bookings page.
 *
 * Matches the global mentor sidebar style: a slim rail where each item is a centered icon with a
 * small label beneath it (the hover tooltip carries the longer description). A "Help & Calls" button
 * is pinned to the bottom — it replaces the global floating intercom button on this page.
 *
 * Purely presentational; the parent computes the role-filtered `items` and owns the selected state.
 * Reused verbatim in the desktop rail and the mobile drawer.
 */
const EMERALD = {
  selectedText: 'rgba(6, 95, 70, 0.95)',
  selectedBg: 'rgba(16, 185, 129, 0.12)',
  selectedIcon: 'rgba(16, 185, 129, 0.95)',
  hoverBg: 'rgba(16, 185, 129, 0.06)',
  idleIcon: 'rgba(71, 85, 105, 0.9)',
  idleText: 'rgba(15, 23, 42, 0.8)',
}

function MenuItemButton({ icon: Icon, label, description, selected, onClick }) {
  return (
    <Tooltip title={description || label} placement="right" arrow disableInteractive>
      <Box
        role="button"
        tabIndex={0}
        aria-current={selected ? 'page' : undefined}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClick?.()
          }
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
          padding: '10px 4px',
          borderRadius: '12px',
          color: selected ? EMERALD.selectedText : EMERALD.idleText,
          backgroundColor: selected ? EMERALD.selectedBg : 'transparent',
          transition: 'all 0.15s ease',
          '&:hover': { backgroundColor: selected ? EMERALD.selectedBg : EMERALD.hoverBg },
          '&:focus-visible': {
            outline: `2px solid ${EMERALD.selectedIcon}`,
            outlineOffset: '1px',
          },
        }}
      >
        {Icon && (
          <Icon sx={{ fontSize: 24, color: selected ? EMERALD.selectedIcon : EMERALD.idleIcon }} />
        )}
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: selected ? 700 : 600,
            lineHeight: 1.2,
            textAlign: 'center',
            color: 'inherit',
          }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  )
}

function ClinicBookingsLeftMenu({ items = [], selectedId, onSelect, onOpenIntercom }) {
  return (
    <Box
      component="nav"
      aria-label="Clinic bookings sections"
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {items.map((item) => (
          <MenuItemButton
            key={item?.id}
            icon={item?.icon}
            label={item?.title}
            description={item?.description}
            selected={item?.id === selectedId}
            onClick={() => onSelect?.(item?.id)}
          />
        ))}
      </Box>

      {onOpenIntercom && (
        <Box sx={{ mt: 'auto', pt: 1.5 }}>
          <Box sx={{ borderTop: '1px solid rgba(15, 23, 42, 0.08)', pt: 1.5 }}>
            <MenuItemButton
              icon={SupportAgentIcon}
              label="Help & Calls"
              description="Chat and calls"
              selected={false}
              onClick={onOpenIntercom}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default memo(ClinicBookingsLeftMenu)
